import type { Config } from '@dashboard/types/config'
import api from '@dashboard/services/axiosApi'
import { useConfigStore } from '@dashboard/stores/configStore'
import { storeToRefs } from 'pinia'
import { ref, toRaw, watch } from 'vue'
import {
  analyzeChanges as analyzeSharedChanges,
  analyzeConfigOnlyChanges as analyzeSharedConfigOnlyChanges,
  checkSplitTestChanges,
  checkIntegrationChanges,
  checkTeamSettingsChanges,
  formatChangeSummary,
  generateCommitMessage,
  hasChanges as hasSharedChanges,
  hostedConfigChanges,
  stripHtmlFromDiff
} from '@shared/configChanges'
import type { ConfigChange, HostedChange, PublishContext } from '@shared/configChanges'

export type { ConfigChange } from '@shared/configChanges'

type Integration = PublishContext['integrations']['v1'][number]
type IntegrationConnection = PublishContext['integrations']['v2'][number]

// Module-level cache (shared across all component instances)
const cachedIntegrations = ref<{ v1: Integration[]; v2: IntegrationConnection[] }>({ v1: [], v2: [] })
const integrationsLastFetched = ref<number>(0)
const integrationsFetched = ref(false) // Track if we've fetched at all
const CACHE_DURATION = 10000 // 10 seconds - long enough to avoid rapid refetches during editing

// Track pending fetches to avoid duplicate requests (module-level)
let pendingIntegrationsFetch: Promise<{ v1: Integration[]; v2: IntegrationConnection[] }> | null = null

export function useConfigChanges() {
  const configStore = useConfigStore()
  const { currentTeamPid, currentTeam } = storeToRefs(configStore)

  // Invalidate cache when team changes (only if currentTeamPid is defined)
  watch(
    () => currentTeamPid?.value,
    () => {
      cachedIntegrations.value = { v1: [], v2: [] }
      integrationsLastFetched.value = 0
      integrationsFetched.value = false
      pendingIntegrationsFetch = null
    }
  )

  // Fetch integrations from the API (both V1 and V2) with caching
  const fetchIntegrations = async (): Promise<{ v1: Integration[]; v2: IntegrationConnection[] }> => {
    const now = Date.now()

    // Return cached data if still valid (check if fetched, not if data exists)
    if (integrationsFetched.value && now - integrationsLastFetched.value < CACHE_DURATION) {
      return cachedIntegrations.value
    }

    // Return pending fetch if one is in progress
    if (pendingIntegrationsFetch) {
      return pendingIntegrationsFetch
    }

    // Start new fetch
    if (!currentTeamPid.value) {
      return { v1: [], v2: [] }
    }

    pendingIntegrationsFetch = (async () => {
      try {
        const team = configStore.currentTeam
        const usesV2 = team?.uses_v2_integrations

        let result: { v1: Integration[]; v2: IntegrationConnection[] }

        // Fetch V2 integrations
        if (usesV2) {
          const response = await api.get(`/api/${currentTeamPid.value}/connections`)
          const partners = response.data.partners || []
          // Filter to only integrations that actually exist and have timestamps
          const existingConnections = partners.filter((p: IntegrationConnection) => p.hasExistingIntegration)
          result = { v1: [], v2: existingConnections }
        } else {
          // Fetch V1 integrations
          const response = await api.get(`/api/${currentTeamPid.value}/integrations/partners`)
          const partners = response.data.partners || []

          // Filter to only integrations that exist and have timestamps
          const existingIntegrations = partners.filter((p: Integration) => p.hasExistingIntegration)

          result = { v1: existingIntegrations, v2: [] }
        }

        cachedIntegrations.value = result
        integrationsLastFetched.value = Date.now()
        integrationsFetched.value = true
        return result
      } catch (error) {
        console.error('Error fetching integrations:', error)
        return { v1: [], v2: [] }
      } finally {
        pendingIntegrationsFetch = null
      }
    })()

    return pendingIntegrationsFetch
  }

  const publishContext = (splitTests: PublishContext['splitTests'] = []): PublishContext => {
    const team = currentTeam.value
    return {
      splitTests,
      integrations: cachedIntegrations.value,
      team: team
        ? {
            last_published_at: team.last_published_at,
            recaptcha_key: team.recaptcha_key,
            published_recaptcha_key: team.published_recaptcha_key,
            has_recaptcha_secret: Boolean(team.recaptcha_secret),
            uses_v2_integrations: team.uses_v2_integrations
          }
        : null
    }
  }

  const analyzeChanges = async (
    currentConfig: Config,
    publishedConfig: Config | null,
    splitTests: PublishContext['splitTests'],
    hosted: { changes: readonly HostedChange[]; servingHost: string | null } | null
  ): Promise<ConfigChange[]> => {
    const hostedChanges = hosted ? hostedConfigChanges(hosted.changes, hosted.servingHost) : []
    // First publish has never included split tests or integrations in the change list.
    if (!publishedConfig) {
      return [...analyzeSharedChanges(currentConfig, publishedConfig, publishContext()), ...hostedChanges]
    }

    const configChanges = analyzeSharedConfigOnlyChanges(currentConfig, publishedConfig)
    const splitTestChanges = checkSplitTestChanges(currentConfig, publishContext(splitTests))
    const integrations = await fetchIntegrations()
    const integrationChanges = checkIntegrationChanges({ ...publishContext(), integrations })
    const teamSettingsChanges = checkTeamSettingsChanges(publishContext().team)
    return [...configChanges, ...splitTestChanges, ...integrationChanges, ...teamSettingsChanges, ...hostedChanges]
  }

  // Retain the existing first-publish team-settings behavior of this public API.
  const analyzeConfigOnlyChanges = (currentConfig: Config, publishedConfig: Config | null): ConfigChange[] =>
    publishedConfig
      ? analyzeSharedConfigOnlyChanges(currentConfig, publishedConfig)
      : analyzeSharedChanges(currentConfig, publishedConfig, publishContext())

  const hasChanges = (currentConfig: Config | null, publishedConfig: Config | null): boolean =>
    hasSharedChanges(toRaw(currentConfig), toRaw(publishedConfig))

  return {
    analyzeChanges,
    analyzeConfigOnlyChanges,
    formatChangeSummary,
    generateCommitMessage,
    hasChanges,
    stripHtmlFromDiff
  }
}
